<template>
  <v-container fluid>
    <v-row class="pb-5">
      <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
        <v-select
          label="Report Type*"
          outlined
          dense
          hide-details
          v-model="reportType"
          :items="filteredReportTypes"
          item-text="name"
          item-value="id"
          :no-data-text="'No report types found'"
          :no-results-text="'No results found'"
          @change="getPortCallsReport"
        >
          <template v-slot:prepend-item>
            <v-text-field
              class="px-4"
              v-model="reportSearch"
              prepend-inner-icon="mdi-magnify"
              label="Search"
              hide-details
              @input="filterReportTypes"
            ></v-text-field>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateRangeText"
              label="Filter by ETA Date*"
              readonly
              outlined
              dense
              v-bind="attrs"
              v-on="on"
              hide-details
              prepend-inner-icon="mdi-calendar"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="dateRange"
            range
            scrollable
            @input="filterByDate"
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="menu = false">Close</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="4" class="pb-0">
        <v-btn-toggle
          v-model="rangeType"
          borderless
          color="primary"
        >
          <v-btn value="month" height="40">
            Month
          </v-btn>
          <v-btn value="week" height="40">
            Week
          </v-btn>
          <v-btn value="day" height="40">
            Day
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>
    <v-row v-if="this.reportType && this.dateRangeText && !chartDataLoading" class="pt-6">
      <v-col cols="12">
        <template v-if="isReportForAllWithRangeApplied">
          <stacked-column-chart
            :title="customChartTitle"
            seriesName="Port Calls"
            :xAxisData="xAxisData"
            :yAxisData="yAxisData"
            :colors="colorsArray"
            :showAverage="reportType.includes('on_time_efficiency')"
            :xAxisAverageOntimeefficiency="xAxisAverageOntimeefficiency"
          />
        </template>
        <template v-else>
          <column-chart
            :title="customChartTitle"
            seriesName="Port Calls"
            :xAxisData="xAxisData"
            :yAxisData="yAxisData"
            :showAverage="reportType.includes('on_time_efficiency')"
            :xAxisAverageOntimeefficiency="xAxisAverageOntimeefficiency"
          />
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import moment from 'moment';
  import ColumnChart from './blocks/ColumnChart';
  import StackedColumnChart from './blocks/StackedColumnChart';
  export default {
    name: 's-hr-reports',
    components: {
      ColumnChart,
      StackedColumnChart,
    },
    data() {
      return {
        reportType: '',
        rangeType: '',
        reportData: [],
        reportSearch: '',
        filteredReportTypes: [],
        colorsArray: [],
        dateRange: null,
        dateRangeText: '',
        menu: false,
        xAxisData: [],
        yAxisData: [],
        xAxisAverageOntimeefficiency: {},
        chartDataLoading: false,
        reportTypes: [
          {
            id: 'all_ops_port_calls',
            name: 'Ops. Team Port Calls',
          },
          {
            id: 'all_ops_on_time_efficiency',
            name: 'Ops. Team on-time efficiency',
          },
          {
            id: 'all_manager_port_calls',
            name: "Manager's Port Calls",
          },
          {
            id: 'all_manager_on_time_efficiency',
            name: "Manager's on-time efficiency",
          },
        ],
      };
    },
    computed: {
      customChartTitle() {
        const reportTypeName = this.reportTypes.find(reportType => reportType.id === this.reportType)?.name;
        return reportTypeName + ' Report' + ` (${this.dateRangeText})`;
      },
      isReportForAllWithRangeApplied() {
        return (this.reportType.split('_')[0] === 'all' && this.rangeType) || this.reportType.includes('on_time_efficiency');
      }
    },
    methods: {
      formatOnTimeEfficiencyData(data) {
        return data.map(item => {
          const onTimeCount = item.operation_count - item.overdue_count;
          const efficiency =
            item.operation_count > 0
              ? ((onTimeCount / item.operation_count) * 100).toFixed(2)
              : "0.00";
          return {
            ...item,
            on_time_count: onTimeCount,
            on_time_efficiency: parseFloat(efficiency),
          };
        });
      },
      getPortCallsReport() {
        if (!this.reportType || !this.dateRangeText) return;
        this.chartDataLoading = true;
        this.colorsArray = [];
        /**
         * filterType: 'ops' or 'manager'
         * reportFor: 'all' or specific id
         * metricFor: 'port_calls' or 'on_time_efficiency'
         * dateRangeFilter: 'start_date,end_date'
         * groupBy: 'ops', 'manager', 'month', 'week', 'day'
         */
        const filter = {
          filterType: this.reportType.includes('ops') ? 'ops' : 'manager',
          reportFor: this.reportType.split('_')[0],
          metricFor: this.reportType.includes('on_time_efficiency') ? 'on_time_efficiency' : 'port_calls',
          dateRangeFilter: this.dateRange.sort().join(','),
        };

        filter.groupBy = this.rangeType || filter.filterType;

        this.$api.reports.getPortCallsReport(filter)
          .then((response) => {
            this.xAxisAverageOntimeefficiency = {};
            if (this.rangeType && this.reportType.split('_')[0] === 'all' && !this.reportType.includes('on_time_efficiency')) {
              // Handle stacked chart for all users with time-based grouping
              this.processStackedTimeBasedData(response.port_calls_report);
            } else if (this.rangeType && this.reportType.split('_')[0] === 'all' && this.reportType.includes('on_time_efficiency')) {
              // Use efficiency calculation for time-based data
              this.processStackedTimeBasedData(response.port_calls_report);
            } else if (this.rangeType && this.reportType.split('_')[0] !== 'all' && !this.reportType.includes('on_time_efficiency')) {
              // Handle time-based grouping (month/week/day)
              this.processTimeBasedData(response.port_calls_report);
            } else if (!this.rangeType && this.reportType.split('_')[0] !== 'all' && this.reportType.includes('on_time_efficiency')) {
              const result = this.formatOnTimeEfficiencyData(response.port_calls_report);
              this.xAxisData = result.map(item => item.name);
              this.yAxisData = [
                {
                  name: 'On Time',
                  data: result.map(item => item.on_time_count)
                },
                {
                  name: 'Overdue',
                  data: result.map(item => item.overdue_count)
                }
              ];
              this.colorsArray = ['#81C784', '#E57373'];
              this.xAxisData.forEach((xAxisLabel, index) => {
                this.xAxisAverageOntimeefficiency[xAxisLabel] = result[index].on_time_efficiency;
              });
            } else if (this.rangeType && this.reportType.split('_')[0] !== 'all' && this.reportType.includes('on_time_efficiency')) {
              const result = this.formatOnTimeEfficiencyData(response.port_calls_report);
              this.processTimeBasedData(result);
              this.colorsArray = ['#81C784', '#E57373'];
            } else if (!this.rangeType && this.reportType.split('_')[0] === 'all' && this.reportType.includes('on_time_efficiency')) {
              let result = this.formatOnTimeEfficiencyData(response.port_calls_report);
              result = result.sort((a, b) => b.on_time_efficiency - a.on_time_efficiency);
            
              this.xAxisData = result.map(item => item.name);
              this.yAxisData = [
                {
                  name: 'On Time',
                  data: result.map(item => item.on_time_count)
                },
                {
                  name: 'Overdue',
                  data: result.map(item => item.overdue_count)
                }
              ];
              this.colorsArray = ['#81C784', '#E57373'];
              this.xAxisData.forEach((xAxisLabel, index) => {
                this.xAxisAverageOntimeefficiency[xAxisLabel] = result[index].on_time_efficiency;
              });
            } else {
              this.xAxisData = response.port_calls_report.map(item => item.name);
              this.yAxisData = response.port_calls_report.map(item => item.operation_count);
            }
            this.chartDataLoading = false;
          });
      },
      filterByDate() {
        if (this.dateRange && this.dateRange.length === 2) {
          const [startDate, endDate] = this.dateRange.sort();
          const fmt = (d) => moment(d, 'YYYY-MM-DD').format('DD-MM-YYYY');
          this.dateRangeText = `${fmt(startDate)} - ${fmt(endDate)}`;
          this.getPortCallsReport();
        } else {
          this.dateRangeText = '';
        }
      },
      filterReportTypes() {
        this.filteredReportTypes = this.reportTypes.filter(reportType => {
          return reportType.name.toLowerCase().includes(this.reportSearch.toLowerCase());
        });
      },
      processStackedTimeBasedData(data) {
        // Group data by time_period and user
        const timePeriodData = {};
        const userNames = new Set();
        
        data.forEach(item => {
          const timePeriod = item.time_period;
          const userName = item.user_name;
          
          userNames.add(userName);
          
          if (!timePeriodData[timePeriod]) {
            timePeriodData[timePeriod] = {};
          }
          
          // Store both operation_count and overdue_count for efficiency calculation
          timePeriodData[timePeriod][userName] = {
            operation_count: item.operation_count,
            overdue_count: item.overdue_count,
            on_time_count: item.operation_count - item.overdue_count
          };
        });

        // Get unique time periods and sort them
        const sortedTimePeriods = Object.keys(timePeriodData).sort((a, b) => 
          new Date(a) - new Date(b)
        );

        // Format x-axis data (week ranges)
        this.xAxisData = sortedTimePeriods.map(timePeriod => {
          const date = new Date(timePeriod);
          
          switch (this.rangeType) {
            case 'week':
              // Calculate end of week (Sunday)
              const endOfWeek = new Date(date);
              endOfWeek.setDate(date.getDate() + 6);
              
              const startMonth = date.toLocaleDateString('en-US', { month: 'short' });
              const startDay = date.getDate();
              const startYear = date.getFullYear();
              
              const endMonth = endOfWeek.toLocaleDateString('en-US', { month: 'short' });
              const endDay = endOfWeek.getDate();
              const endYear = endOfWeek.getFullYear();
              
              return `${startMonth} ${startDay} ${startYear} - ${endMonth} ${endDay} ${endYear}`;
              
            case 'month':
              return date.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              });
              
            case 'day':
              return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              });
              
            default:
              return timePeriod;
          }
        });

        // Create series data for each user
        const sortedUserNames = Array.from(userNames).sort();
        
        if (this.reportType.includes('on_time_efficiency')) {
          this.yAxisData = [
            {
              name: 'On Time',
              data: sortedTimePeriods.map(timePeriod => {
                const users = Object.values(timePeriodData[timePeriod] || {});
                const totalOnTime = users.reduce(
                  (sum, user) => sum + (user.on_time_count || 0),
                  0
                );
                return totalOnTime;
              })
            },
            {
              name: 'Overdue',
              data: sortedTimePeriods.map(timePeriod => {
                const users = Object.values(timePeriodData[timePeriod] || {});
                const totalOverdue = users.reduce(
                  (sum, user) => sum + (user.overdue_count || 0),
                  0
                );
                return totalOverdue;
              })
            }
          ];
          this.colorsArray = ['#81C784', '#E57373'];
          // Calculate average efficiency for each time period
          const xAxisAverageOntimeefficiency = {};
          this.xAxisData.forEach((xAxisLabel, index) => {
            const timePeriod = sortedTimePeriods[index];
            let totalOperations = 0;
            let totalOnTimeOperations = 0;
            
            sortedUserNames.forEach(userName => {
              const userData = timePeriodData[timePeriod][userName];
              if (userData && userData.operation_count > 0) {
                totalOperations += userData.operation_count;
                totalOnTimeOperations += userData.on_time_count;
              }
            });
            
            if (totalOperations > 0) {
              const averageEfficiency = ((totalOnTimeOperations / totalOperations) * 100).toFixed(2);
              xAxisAverageOntimeefficiency[xAxisLabel] = parseFloat(averageEfficiency);
            } else {
              xAxisAverageOntimeefficiency[xAxisLabel] = 0;
            }
          });
          this.xAxisAverageOntimeefficiency = xAxisAverageOntimeefficiency;
        } else {
          this.yAxisData = sortedUserNames.map(userName => ({
            name: userName,
            data: sortedTimePeriods.map(timePeriod => 
              timePeriodData[timePeriod][userName]?.operation_count || 0
            )
          }));
        }
      },
      processTimeBasedData(data) {
        // Format x-axis data based on rangeType
        this.xAxisData = data.map(item => {
          const date = new Date(item.time_period);
          
          switch (this.rangeType) {
            case 'week':
              // Calculate end of week (Sunday)
              const endOfWeek = new Date(date);
              endOfWeek.setDate(date.getDate() + 6);
              
              const startMonth = date.toLocaleDateString('en-US', { month: 'short' });
              const startDay = date.getDate();
              const startYear = date.getFullYear();
              
              const endMonth = endOfWeek.toLocaleDateString('en-US', { month: 'short' });
              const endDay = endOfWeek.getDate();
              const endYear = endOfWeek.getFullYear();
              
              return `${startMonth} ${startDay} ${startYear} - ${endMonth} ${endDay} ${endYear}`;
              
            case 'month':
              return date.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              });
              
            case 'day':
              return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              });
              
            default:
              return item.time_period;
          }
        });

        if (this.reportType.includes('on_time_efficiency')) {
          this.yAxisData = [
            {
              name: 'On Time',
              data: data.map(item => item.on_time_count)
            },
            {
              name: 'Overdue',
              data: data.map(item => item.overdue_count)
            }
          ];
          this.xAxisData.forEach((xAxisLabel, index) => {
            const onTimeEfficiency = data[index].on_time_efficiency;
            this.xAxisAverageOntimeefficiency[xAxisLabel] = onTimeEfficiency;
          });
        } else {
          this.yAxisData = data.map(item => item.operation_count);
        }
      },
      getUsers() {
        this.$api.users.index()
          .then((response) => {
            response.users.forEach(user => {
              this.reportTypes.push({
                id: `${user.id}_ops_port_calls`,
                name: `Ops. ${user.name}'s Port Calls${user.is_active ? '' : '(Inactive)'}`,
              });
              this.reportTypes.push({
                id: `${user.id}_ops_on_time_efficiency`,
                name: `Ops. ${user.name}'s on-time efficiency${user.is_active ? '' : '(Inactive)'}`,
              });
              if (user.role === 'manager') {
                this.reportTypes.push({
                  id: `${user.id}_manager_port_calls`,
                  name: `Manager's ${user.name}'s Port Calls${user.is_active ? '' : '(Inactive)'}`,
                });
                this.reportTypes.push({
                  id: `${user.id}_manager_on_time_efficiency`,
                  name: `Manager's ${user.name}'s on-time efficiency${user.is_active ? '' : '(Inactive)'}`,
                });
              }
            });
          }).catch((err) => {
            this.$store.dispatch('setAlert', {
              status: 'error',
              show: true,
              message: err.message,
            });
          });
      },
    },
    watch: {
      rangeType: {
        handler() {
          this.getPortCallsReport();
        },
      },
    },
    created() {
      if (!this.$root.hasAccess('')) {
        this.$store.dispatch('setAlert', {
          status: 'error',
          show: true,
          message: "Please contact admin to access this page",
        });
        this.$router.push({name: 'home'});
      } else {
        this.reportType = this.reportTypes[0].id;
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        this.dateRange = [thirtyDaysAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]];
        const fmt = (d) => moment(d, 'YYYY-MM-DD').format('DD-MM-YYYY');
        this.dateRangeText = `${fmt(this.dateRange[0])} - ${fmt(this.dateRange[1])}`;
        this.getPortCallsReport();
        this.getUsers();
        this.filteredReportTypes = this.reportTypes;
      }
    }
  };
</script>